// client/src/utils/notificationFormatter.js

/**
 * Determine if a notification is for a doubles match
 * @param {Object} payload - Notification payload
 * @returns {boolean} True if this is a doubles match notification
 */
function isDoublesNotification(payload) {
  return !!(
    payload?.proposer_team_id ||
    payload?.opponent_team_id ||
    payload?.home_team_id ||
    payload?.away_team_id
  )
}

/**
 * Format opponent/player reference for display, handling both singles and doubles
 * @param {Object} payload - Notification payload
 * @param {string} opponentName - Opponent name from backend (may be team captain)
 * @returns {string} Formatted opponent/team reference
 */
function formatOpponentReference(payload, opponentName) {
  if (!payload) return 'the opponent'

  const isDoubles = isDoublesNotification(payload)

  if (!opponentName) {
    return isDoubles ? 'the opposing team' : 'the opponent'
  }

  // For doubles, indicate it's a team match
  // Backend sends the team captain/primary player name
  if (isDoubles) {
    return `${opponentName}'s team`
  }

  return opponentName
}

/**
 * Format notification text based on type and payload
 * @param {Object} notification - Notification object
 * @returns {string} Formatted notification text
 */
export function formatNotificationText(notification) {
  const { type, payload } = notification

  switch (type) {
    case 'match_proposal':
      if (payload?.opponent_name || isDoublesNotification(payload)) {
        return `Match proposal from ${formatOpponentReference(payload, payload?.opponent_name)}`
      }
      return 'You have a new match proposal'

    case 'match_proposal_accepted':
      if (payload?.opponent_name || isDoublesNotification(payload)) {
        return `Match proposal accepted by ${formatOpponentReference(payload, payload?.opponent_name)}`
      }
      return 'Your match proposal has been accepted'

    case 'match_proposal_declined':
      if (payload?.opponent_name || isDoublesNotification(payload)) {
        return `Match proposal declined by ${formatOpponentReference(payload, payload?.opponent_name)}`
      }
      return 'Your match proposal has been declined'

    case 'match_proposal_expired':
      return isDoublesNotification(payload)
        ? 'A doubles match proposal expired and was canceled'
        : 'A match proposal expired and was canceled'

    case 'match_score_submitted':
      // Include submitter name if available in payload
      if (payload?.submitter_name) {
        return `Match score submitted by ${payload.submitter_name}`
      }
      // Backward compatibility for older payloads; doubles-aware formatting
      if (payload?.opponent_name || isDoublesNotification(payload)) {
        return `Match score submitted by ${formatOpponentReference(payload, payload?.opponent_name)}`
      }
      return 'A match score has been submitted'

    case 'league_update':
      // Include league name if available in payload
      if (payload?.league_name) {
        return `${payload.league_name} update available`
      }
      return 'League update available'

    case 'chat_message':
      // Include sender name if available in payload
      if (payload?.sender_name) {
        return `New message from ${payload.sender_name}`
      }
      return 'New chat message'

    case 'doubles_registration':
      if (payload?.registrant_name && payload?.league_name) {
        return `${payload.registrant_name} added you as their doubles partner in ${payload.league_name}`
      }
      if (payload?.registrant_name) {
        return `${payload.registrant_name} added you as their doubles partner`
      }
      return 'You were added as a doubles partner'

    default:
      return 'New notification'
  }
}

/**
 * Get navigation path for a notification
 * @param {Object} notification - Notification object
 * @returns {string|null} Navigation path or null if not navigable
 */
export function getNotificationNavigationPath(notification) {
  const { type, payload } = notification

  if (!payload) return null

  switch (type) {
    case 'match_proposal':
      // Navigate to proposals inbox for the specific league
      if (payload.league_id) {
        return `/leagues/${payload.league_id}/proposals`
      }
      // Fallback to scheduled matches page
      return '/scheduled-matches'

    case 'match_proposal_accepted':
      // Navigate to match central page to view the scheduled match
      if (payload.league_id && payload.match_id) {
        return `/leagues/${payload.league_id}/match/${payload.match_id}/score`
      }
      // Fallback to scheduled matches page
      return '/scheduled-matches'

    case 'match_proposal_declined':
      if (payload.league_id) {
        return `/leagues/${payload.league_id}/proposals`
      }
      return '/scheduled-matches'

    case 'match_proposal_expired':
      if (payload.league_id) {
        return `/leagues/${payload.league_id}/proposals`
      }
      return '/scheduled-matches'

    case 'match_score_submitted':
      // Navigate to match score entry page to view/verify the submitted score
      if (payload.league_id && payload.match_id) {
        return `/leagues/${payload.league_id}/match/${payload.match_id}/score`
      }
      // Fallback to scheduled matches page
      return '/scheduled-matches'

    case 'league_update':
      if (payload.league_id) {
        return `/leagues/${payload.league_id}`
      }
      return null

    case 'chat_message':
      // Navigate to chat
      return '/chat'

    default:
      return null
  }
}

/**
 * Format relative time for notification timestamp
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted relative time
 */
export function formatRelativeTime(timestamp) {
  // Handle null/undefined
  if (timestamp === null || timestamp === undefined) {
    return 'Unknown time'
  }

  const date = new Date(timestamp)

  // Validate the date
  if (isNaN(date.getTime())) {
    return 'Unknown time'
  }

  const now = new Date()
  const diffMs = now - date

  // Handle future timestamps (clock skew or incorrect server time)
  if (diffMs < 0) {
    return 'Just now'
  }

  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  // For older notifications, show the date
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
